import { useEffect, useMemo, useState } from 'react';

import { CallSplit } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CampaignChannels, Send } from 'domains/campaigns/types';
import { getCampaignSendsForCriteriaBuilder } from 'domains/campaigns/utils';
import FormLayout from 'domains/core/components/FormLayout';
import Select, { getOptionValues, Value } from 'domains/core/components/Select';
import SegmentCriteriaModeBuilder from 'domains/segments/components/SegmentCriteriaModeBuilder';
import ActivityFilter from 'domains/segments/pages/SegmentCreatePage/Filter/ActivityFilter';
import DemographicFilter from 'domains/segments/pages/SegmentCreatePage/Filter/DemographicFilter';
import EventFilter from 'domains/segments/pages/SegmentCreatePage/Filter/EventFilter';
import { isValidActivityFilter, isValidEventsFilter } from 'domains/segments/pages/SegmentCreatePage/utils';
import {
    SegmentDefinition,
    SegmentFilterCategorySpecificField,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
    SegmentFilterState,
} from 'domains/segments/types';
import { getDemographicFilters, getEngagementNameToValuesRef, getSegmentDefinition } from 'domains/segments/utils';
import useSegmentFilters from 'hooks/queries/useSegmentFilters';
import useSegments from 'hooks/queries/useSegments';
import useSendById from 'hooks/queries/useSendById';
import useSends from 'hooks/queries/useSends';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus, FilterCategory, RecordType } from 'models/enums';
import { getFriendlyName } from 'utils';

import DisabledTooltipContent, { DisabledTooltip } from '../../../components/DisabledTooltipContent';
import { CustomCampaignSteps } from '../../../CustomCampaign';
import { setSegment, setSplitSegmentDefinition } from '../../../customCampaignActions';
import { CampaignStateTypes } from '../../../customCampaignReducer';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
};

/**
 * Converts a split's legacy single-filter criteria (`send.segments`, always a one-element array —
 * see getUpdatedSendsWithSegments) into a SegmentDefinition, so curations saved before the advanced
 * criteria builder shipped still pre-fill correctly instead of appearing blank.
 */
export const legacySegmentsToDefinition = (segments: any[] | undefined): SegmentDefinition | null => {
    if (!segments?.length) return null;
    return getSegmentDefinition({
        filters: segments,
        hasAllContacts: true,
        matchType: 'AND',
        recordTypes: [],
    }) as SegmentDefinition;
};

/**
 * Dual-mode (saved audience / custom nested AND-OR rules) split criteria builder, at parity with
 * Exit Criteria. Gated behind the `conditional-split-advanced-criteria` LaunchDarkly flag (exposed
 * here as `conditionalSplitAdvancedCriteria` — the SDK's default `useCamelCaseFlagKeys` behaviour
 * camelCases kebab-case flag keys for `useFlags()` consumers) — see LegacySplitCriteriaStep for the
 * flag-off fallback.
 */
const AdvancedSplitCriteriaStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setIsContinueEnabled,
    setNextStep,
}: Props) => {
    const activeSend = campaignState.sends[activeSendIndex];

    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const disableEditing = status === CampaignStatus.PAUSED;

    const { data: segments, isInitialLoading: isSegmentsLoading } = useSegments();

    // Split criteria (contact demographics/activity) is channel-agnostic — the MEETS branch root
    // can be any channel in a mixed-channel curation, so don't gate on isActiveSendEmail here.
    const initialDefinition = useMemo((): SegmentDefinition | null => {
        const existingDefinition = (activeSend as any)?.segmentDefinition;
        if (existingDefinition) return existingDefinition;
        return legacySegmentsToDefinition((activeSend as any)?.segments);
        // Only re-derive when navigating between sends, mirroring the previous step's behaviour —
        // edits made via SegmentCriteriaModeBuilder shouldn't fight this memo on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex]);

    // All of this curation's own email sends, including ones positioned after this split — e.g. its
    // own to-be-created MEETS/DOES_NOT_MEET branch sends and any nested descendants — so they're
    // selectable as engagement targets, mirroring the campaign-wide exit-criteria send picker.
    const campaignSendsForCriteria = useMemo(() => getCampaignSendsForCriteriaBuilder(campaignState.sends), [
        campaignState.sends,
    ]);

    useEffect(() => {
        setIsContinueEnabled(Boolean(initialDefinition));
        setNextStep(CustomCampaignSteps.SELECT_PATH_ACTION);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex]);

    const handleCriteriaChange = (definition: SegmentDefinition | null, valid: boolean) => {
        if (disableEditing) return;
        setIsContinueEnabled(valid);
        if (valid && definition) {
            dispatchCampaignAction(setSplitSegmentDefinition(activeSendIndex, definition));
        }
    };

    return (
        <FormLayout
            header="select split criteria"
            stepName="conditional split"
            icon={<CallSplit />}
            guidingText="Create a split in your Curation based on audience behavior."
        >
            <DisabledTooltipContent disabled={disableEditing}>
                <span>
                    <SegmentCriteriaModeBuilder
                        initialDefinition={initialDefinition}
                        segments={segments}
                        isSegmentsLoading={isSegmentsLoading}
                        campaignSends={campaignSendsForCriteria}
                        audienceLabel="Audience"
                        modeAriaLabel="Split criteria mode"
                        modeTestIdPrefix="split"
                        disabled={disableEditing}
                        onChange={handleCriteriaChange}
                    />
                </span>
            </DisabledTooltipContent>
        </FormLayout>
    );
};

const allContactsRecordType = {
    label: RecordType.ALL_CONTACTS,
    value: RecordType.ALL_CONTACTS,
};

const initialFilterState: SegmentFilterState<SegmentFilterCategorySpecificField> = {
    id: 0,
    category: FilterCategory.CONTACT_ACTIVITY,
    name: '',
    operator: null,
    categorySpecificFields: {
        value: null,
    },
    autocompleteValue: null,
    autocompleteInputValue: '',
};

/**
 * Pre-advanced-criteria single flat-filter split UI. Kept as the `conditional-split-advanced-criteria`
 * flag-off fallback so the split criteria step still works if the advanced builder needs to be
 * rolled back.
 */
const LegacySplitCriteriaStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setIsContinueEnabled,
    setNextStep,
}: Props) => {
    const activeSend = campaignState.sends[activeSendIndex];
    const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;

    const initialCategory =
        (isActiveSendEmail && (activeSend as any)?.segments?.[0]?.category) || FilterCategory.CONTACT_ACTIVITY;
    const [category, setCategory] = useState(initialCategory);
    const [filter, setFilter] = useState(initialFilterState);
    const [datetimeErrors, setDatetimeErrors] = useState({});

    // When a sentinel value is selected (unsaved same-curation send), resolve its templateId so
    // ValueAutocomplete can extract clickable links from the template HTML without a DB record.
    const selectedSendValue = isActiveSendEmail && (filter as any)?.categorySpecificFields?.value;
    const sentinelTemplateId = useMemo(() => {
        if (typeof selectedSendValue === 'number' && selectedSendValue < 0) {
            const idx = -selectedSendValue - 1;
            return (campaignState.sends[idx] as any)?.templateId ?? undefined;
        }
        return undefined;
    }, [selectedSendValue, campaignState.sends]);

    const activeSendValueUsed = isActiveSendEmail && (activeSend as any)?.segments?.[0]?.categorySpecificFields?.value;
    // Negative values are sentinels for unsaved campaign sends — only fetch by ID for real (positive) send IDs
    const realSendId = typeof activeSendValueUsed === 'number' && activeSendValueUsed > 0 ? activeSendValueUsed : null;
    const { data: sendByIdData } = useSendById(realSendId as number, {
        enabled: Boolean(realSendId),
    });
    const { contactObject: hasContactObjectFlag } = useFlags();

    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const disableEditing = status === CampaignStatus.PAUSED;

    // Update filter autocomplete values when send data is available
    useEffect(() => {
        if (realSendId && sendByIdData?.name) {
            filter.autocompleteInputValue = sendByIdData.name;
            filter.autocompleteValue = { label: sendByIdData.name, value: realSendId };
            setFilter(filter);
        } else if (typeof activeSendValueUsed === 'number' && activeSendValueUsed < 0) {
            // Sentinel value: resolve display name from campaign state using the encoded parent index
            const parentIdx = -activeSendValueUsed - 1;
            const parentSend = campaignState.sends[parentIdx];
            if (parentSend) {
                const name = (parentSend as any).name || '';
                filter.autocompleteInputValue = name;
                filter.autocompleteValue = { label: name, value: activeSendValueUsed };
                setFilter(filter);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realSendId, sendByIdData, activeSendValueUsed]);

    const filterCategories: Value[] = Object.values(FilterCategory)
        .filter((filterCategory) => hasContactObjectFlag || filterCategory !== FilterCategory.CONTACT_OBJECTS)
        .map((filterCategory) => ({ label: filterCategory, value: filterCategory }));

    const { data: segmentFilters } = useSegmentFilters({
        version: 2,
    });

    const engagementFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        // RecordTypes[0] represents engagement filters because there's a single item and its recordType value is "" from the API
        return segmentFilters.engagement.recordTypes[0].filters;
    }, [segmentFilters]);

    const engagementNameToValuesRef = useMemo(() => getEngagementNameToValuesRef(engagementFilters), [
        engagementFilters,
    ]);

    // Derived directly from filter state so it's always reactive — no ref needed.
    const sendsPath = useMemo(() => (filter?.name && engagementNameToValuesRef[filter.name]) || '', [
        filter?.name,
        engagementNameToValuesRef,
    ]);

    const { data: sends, isLoading: isLoadingSends } = useSends({
        path: sendsPath,
        reactQueryOptions: { enabled: Boolean(sendsPath) },
    });

    // Parent email sends from this curation (before the current split send).
    // Sends without a DB id (brand-new unsaved campaign) get a negative sentinel -(originalIndex+1)
    // where originalIndex is the send's position in the FULL campaignState.sends array (before the
    // EMAIL-only filter is applied). This must match how all decode sites interpret the sentinel:
    //   - getSegmentsForApi: parentSendIndex = -rawValue - 1, then sendResults[parentSendIndex]
    //   - resolveParentSendIndexInSegments: sendIndexToId[parentSendIndex]
    //   - display (this file): campaignState.sends[parentIdx]
    // All three index into the full sends array, so the sentinel must encode the full-array position.
    const parentEmailSendsFromState = useMemo(
        (): Send[] =>
            campaignState.sends
                .slice(0, activeSendIndex)
                .map((send, originalIndex) => ({ send, originalIndex }))
                .filter(({ send }) => send.channel === CampaignChannels.EMAIL)
                .map(({ send, originalIndex }) => {
                    const existingId = (send as any).id;
                    return ({
                        id: existingId ?? -(originalIndex + 1),
                        name: (send as any).name || '',
                        status: (send as any).status || 'DRAFT',
                    } as unknown) as Send;
                }),
        [campaignState.sends, activeSendIndex]
    );

    // Merge API sends with parent sends from campaign state, deduplicating by id.
    // This ensures the current curation's sends appear in the dropdown regardless of their
    // DB status (DRAFT sends are excluded by the API query's status filter).
    const allSendsForDropdown = useMemo((): Send[] => {
        const apiSends = sends || [];
        const apiSendIds = new Set(apiSends.map((s) => s.id));
        const uniqueParentSends = parentEmailSendsFromState.filter((s) => !apiSendIds.has(s.id));
        return [...uniqueParentSends, ...apiSends];
    }, [parentEmailSendsFromState, sends]);

    useEffect(() => {
        // Find EMAIL send with segments (may be different from activeSendIndex)
        let segmentsToUse: any[] | undefined;

        if (isActiveSendEmail && (activeSend as any)?.segments?.length) {
            segmentsToUse = (activeSend as any).segments;
        } else {
            // Only look at sibling sends (same parent) so a nested split's form doesn't
            // pre-fill with the outer split's criteria.
            const sendWithSegments = campaignState.sends.find(
                (send, idx) =>
                    idx !== activeSendIndex &&
                    (send as any).parentIndex === (activeSend as any)?.parentIndex &&
                    send.channel === CampaignChannels.EMAIL &&
                    'segments' in send &&
                    (send as any).segments?.length
            ) as any;
            segmentsToUse = sendWithSegments?.segments;
        }

        // @ts-ignore
        if (segmentsToUse?.length) {
            const firstSegment = segmentsToUse[0];
            setFilter(firstSegment);
            if (firstSegment?.category) {
                setCategory(firstSegment.category);
            }
        }

        setNextStep(CustomCampaignSteps.SELECT_PATH_ACTION);
        // The dependency array only contains `activeSendIndex` because we only want it to run
        // if the component rerenders when we're switching from one split to another in the nav.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSendIndex]);

    useEffect(() => {
        const isValidActivitySegment =
            filter?.category === FilterCategory.CONTACT_ACTIVITY && isValidActivityFilter(filter as any);
        const isValidEventSegment =
            (filter?.category === FilterCategory.CONTACT_EVENTS ||
                filter?.category === FilterCategory.CONTACT_OBJECTS) &&
            isValidEventsFilter(filter);

        const hasDatetimeError = Object.values(datetimeErrors).includes(true);
        const isValidDemographicSegment =
            filter?.category === FilterCategory.CONTACT_DETAILS &&
            !!filter?.operator &&
            (!!filter?.autocompleteInputValue?.length || !!filter?.autocompleteValue) &&
            !hasDatetimeError;

        setIsContinueEnabled(isValidActivitySegment || isValidDemographicSegment || isValidEventSegment);
    }, [filter, setIsContinueEnabled, datetimeErrors]);

    const eventFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const titleCasedRecordTypeFilters = segmentFilters.contactEvent.recordTypes?.map(({ recordType, filters }) => ({
            filters,
            recordType: getFriendlyName(recordType),
        }));
        return titleCasedRecordTypeFilters;
    }, [segmentFilters]);

    const objectFilters = useMemo(() => {
        if (!segmentFilters || !hasContactObjectFlag) {
            return [];
        }
        const titleCasedRecordTypeFilters = segmentFilters.contactObject.recordTypes?.map(
            ({ recordType, filters }) => ({
                filters,
                recordType: getFriendlyName(recordType),
            })
        );
        return titleCasedRecordTypeFilters;
    }, [segmentFilters, hasContactObjectFlag]);

    const recordTypes = getOptionValues([allContactsRecordType]);
    const segmentRecordTypes = segmentFilters?.demographic?.recordTypes;
    const demographicFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const result = getDemographicFilters({ hasAllContacts: true, recordTypes, segmentRecordTypes });

        return result;
    }, [segmentFilters, recordTypes, segmentRecordTypes]);

    const handleCategorySelection = (e: any) => {
        setCategory(e.target.value);
        setFilter({
            ...initialFilterState,
            category: e.target.value,
        });
    };

    const handleDatetimeError = (filterId: string, error: boolean) =>
        setDatetimeErrors({ ...datetimeErrors, [filterId]: error });

    const handleUpdateSegmentFilter = (
        _index: number,
        updatedFilter: SegmentFilterState<SegmentFilterCategorySpecificField>
    ) => {
        setFilter(updatedFilter);
        dispatchCampaignAction(setSegment(updatedFilter, activeSendIndex));
    };

    return (
        <FormLayout
            header="select split criteria"
            stepName="conditional split"
            icon={<CallSplit />}
            guidingText="Create a split in your Curation based on audience behavior."
        >
            <Stack alignItems="flex-start" columnGap={2} direction="row" flexWrap="wrap" rowGap={2}>
                <DisabledTooltipContent disabled={disableEditing}>
                    <span>
                        <Select
                            label="Filter type"
                            disabled={disableEditing}
                            sx={{ textTransform: 'capitalize', width: '12rem' }}
                            value={category}
                            values={filterCategories}
                            onChange={handleCategorySelection}
                        />
                    </span>
                </DisabledTooltipContent>
                {category === FilterCategory.CONTACT_ACTIVITY && engagementFilters && (
                    <ActivityFilter
                        filter={filter as SegmentFilterState<SegmentFilterEngagementFields>}
                        index={0}
                        engagementFilters={engagementFilters}
                        sends={allSendsForDropdown}
                        isOtherSendsLoading={isLoadingSends}
                        sx={{ autocomplete: { width: '300px' } }}
                        templateId={sentinelTemplateId}
                        updateSegmentFilter={handleUpdateSegmentFilter}
                        disabled={disableEditing}
                        disabledTooltipContent={<DisabledTooltip />}
                    />
                )}
                {category === FilterCategory.CONTACT_DETAILS && demographicFilters && (
                    <DisabledTooltipContent disabled={disableEditing}>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <DemographicFilter
                                filter={filter}
                                index={0}
                                filterOptions={demographicFilters}
                                handleDatetimeError={handleDatetimeError}
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </DisabledTooltipContent>
                )}
                {category === FilterCategory.CONTACT_EVENTS && eventFilters && (
                    <DisabledTooltipContent disabled={disableEditing}>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <EventFilter
                                eventRecordTypeFilters={eventFilters}
                                filter={filter as SegmentFilterState<SegmentFilterEventFields>}
                                index={0}
                                handleDatetimeError={handleDatetimeError}
                                isResponsive
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </DisabledTooltipContent>
                )}
                {hasContactObjectFlag && category === FilterCategory.CONTACT_OBJECTS && objectFilters && (
                    <DisabledTooltipContent disabled={disableEditing}>
                        <Stack direction="row" sx={disableEditing ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <EventFilter
                                eventRecordTypeFilters={objectFilters}
                                filter={filter as SegmentFilterState<SegmentFilterEventFields>}
                                index={0}
                                handleDatetimeError={handleDatetimeError}
                                isResponsive
                                updateSegmentFilter={handleUpdateSegmentFilter}
                            />
                        </Stack>
                    </DisabledTooltipContent>
                )}
            </Stack>
        </FormLayout>
    );
};

/**
 * Gate between the advanced (saved-audience / nested AND-OR rules) split criteria builder and the
 * legacy flat-filter UI it replaced. Kept as two separate components (rather than branching inside
 * one) so each side's hooks are only ever mounted for its own flag state, satisfying
 * react-hooks/rules-of-hooks.
 */
const CustomCampaignSplitCriteriaStep = (props: Props) => {
    const { conditionalSplitAdvancedCriteria: hasConditionalSplitAdvancedCriteriaFlag } = useFlags();

    return hasConditionalSplitAdvancedCriteriaFlag ? (
        <AdvancedSplitCriteriaStep {...props} />
    ) : (
        <LegacySplitCriteriaStep {...props} />
    );
};

export default CustomCampaignSplitCriteriaStep;
